import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getAnalyticsStats, sendAnalyticsMessage } from '../services/api';
import '../styles/AnalyticsAssistant.css';

const SUGGESTIONS = [
  'How did students perform on the test overall?',
  'Which question was the hardest?',
  'How many questions did students ask on average?',
  'What topics are students most confused about?',
  'How far are students getting through the reading?',
  'Which cycles are students spending the most time on?',
  'Which students haven\'t completed the test yet?',
  'What is the grade distribution across all questions?',
];

// ── Date preset helpers ───────────────────────────────────────────────────────

const PRESETS = [
  { id: 'all',   label: 'All time' },
  { id: '7d',    label: 'Last 7 days' },
  { id: '30d',   label: 'Last 30 days' },
  { id: '90d',   label: 'Last 90 days' },
  { id: 'custom', label: 'Custom' },
];

function presetToDates(preset, customStart, customEnd) {
  if (preset === 'all') return { startDate: null, endDate: null };
  if (preset === 'custom') return { startDate: customStart || null, endDate: customEnd || null };
  const days = parseInt(preset, 10);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function presetToLabel(preset, customStart, customEnd) {
  if (preset === 'all') return 'all time';
  if (preset === '7d') return 'last 7 days';
  if (preset === '30d') return 'last 30 days';
  if (preset === '90d') return 'last 90 days';
  if (preset === 'custom') {
    if (customStart && customEnd) return `${customStart} to ${customEnd}`;
    if (customStart) return `from ${customStart}`;
    return 'custom range';
  }
  return 'all time';
}

// ── localStorage helpers ──────────────────────────────────────────────────────

function chatKey(paperId) {
  return `aa_chat_${paperId}`;
}

function loadMessages(paperId) {
  try {
    const raw = localStorage.getItem(chatKey(paperId));
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return [];
}

function saveMessages(paperId, messages) {
  try {
    localStorage.setItem(chatKey(paperId), JSON.stringify(messages));
  } catch (_) {}
}

// ── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="aa-stat-card">
      {icon && <div className="aa-stat-icon">{icon}</div>}
      <div className="aa-stat-label">{label}</div>
      <div className="aa-stat-value">{value ?? '—'}</div>
      {sub && <div className="aa-stat-sub">{sub}</div>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AnalyticsAssistant({ paper, onBack }) {
  // Conversation (persisted in localStorage)
  const [messages, setMessages] = useState(() => loadMessages(paper?.id));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Time filter
  const [preset, setPreset] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Date helpers ────────────────────────────────────────────────────────────
  const activeDates = presetToDates(preset, customStart, customEnd);
  const activeLabel = presetToLabel(preset, customStart, customEnd);

  // ── Fetch stats ─────────────────────────────────────────────────────────────
  const fetchStats = useCallback(() => {
    if (!paper?.id) return;
    setStatsLoading(true);
    const { startDate, endDate } = presetToDates(preset, customStart, customEnd);
    getAnalyticsStats(paper.id, startDate, endDate)
      .then(d => setStats(d))
      .catch(() => setStats({ error: true }))
      .finally(() => setStatsLoading(false));
  }, [paper?.id, preset, customStart, customEnd]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── Persist messages ────────────────────────────────────────────────────────
  useEffect(() => {
    if (paper?.id) saveMessages(paper.id, messages);
  }, [messages, paper?.id]);

  // ── Auto-scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ─────────────────────────────────────────────────────────────
  const handleSend = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const { startDate, endDate } = activeDates;
      const data = await sendAnalyticsMessage(paper.id, text, messages, startDate, endDate);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble fetching your analytics data. Please try again.',
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  // ── Clear conversation ────────────────────────────────────────────────────────
  const handleClear = () => {
    setMessages([]);
    if (paper?.id) localStorage.removeItem(chatKey(paper.id));
  };

  // ── Preset change ─────────────────────────────────────────────────────────────
  const handlePresetChange = (id) => {
    setPreset(id);
    setShowCustom(id === 'custom');
  };

  // ── Stat shortcuts ────────────────────────────────────────────────────────────
  const testStats = stats?.stats?.test;
  const cycleStats = stats?.stats?.cycle;
  const qaStats = stats?.stats?.qa;

  return (
    <div className="aa-panel">
      {/* ── Header ── */}
      <div className="aa-header">
        <div className="aa-header-left">
          <button type="button" className="aa-back-btn" onClick={onBack}>
            ← Back
          </button>
          <div className="aa-header-title">
            <span className="aa-header-icon">✦</span>
            <span>Analytics Assistant</span>
          </div>
          <span className="aa-header-paper">{paper?.name}</span>
        </div>
        <div className="aa-header-actions">
          {messages.length > 0 && (
            <button
              type="button"
              className="aa-clear-btn"
              title="Clear conversation history"
              onClick={handleClear}
            >
              Clear chat
            </button>
          )}
          <button
            type="button"
            className="aa-refresh-btn"
            title="Refresh class data"
            onClick={fetchStats}
          >
            ↻ Refresh data
          </button>
        </div>
      </div>

      {/* ── Time filter bar ── */}
      <div className="aa-filter-bar">
        <span className="aa-filter-label">Time period:</span>
        <div className="aa-filter-chips">
          {PRESETS.map(p => (
            <button
              key={p.id}
              type="button"
              className={`aa-filter-chip${preset === p.id ? ' aa-filter-chip--active' : ''}`}
              onClick={() => handlePresetChange(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {showCustom && (
          <div className="aa-filter-custom">
            <input
              type="date"
              className="aa-date-input"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              title="Start date"
            />
            <span className="aa-date-sep">→</span>
            <input
              type="date"
              className="aa-date-input"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              title="End date"
            />
          </div>
        )}
        {preset !== 'all' && (
          <span className="aa-filter-active-label">
            Showing data for: <strong>{activeLabel}</strong>
          </span>
        )}
      </div>

      {/* ── Stat cards strip ── */}
      <div className="aa-stat-strip">
        {statsLoading ? (
          <div className="aa-stat-loading">Loading class data…</div>
        ) : stats?.error ? (
          <div className="aa-stat-loading">Could not load stats.</div>
        ) : (
          <>
            <StatCard
              icon="📝"
              label="Avg Test Score"
              value={testStats?.has_data
                ? (testStats.class_average != null ? `${testStats.class_average}%` : 'No grades yet')
                : 'No tests'}
              sub={testStats?.has_data ? `across ${testStats.total_students ?? 0} student${testStats.total_students !== 1 ? 's' : ''}` : null}
            />
            <StatCard
              icon="🔄"
              label="Cycle Completion"
              value={cycleStats?.has_data
                ? `${cycleStats.completed_sessions} / ${cycleStats.total_sessions}`
                : 'No cycles'}
              sub={cycleStats?.has_data ? 'sessions fully completed' : null}
            />
            <StatCard
              icon="💬"
              label="Avg Q&A Messages"
              value={qaStats?.has_data ? qaStats.avg_messages_per_student : 'No Q&A'}
              sub={qaStats?.has_data ? `across ${qaStats.total_students} student${qaStats.total_students !== 1 ? 's' : ''}` : null}
            />
          </>
        )}
      </div>

      {/* ── Chat body ── */}
      <div className="aa-chat-body">
        {messages.length === 0 ? (
          <div className="aa-welcome">
            <div className="aa-welcome-icon">✦</div>
            <h2 className="aa-welcome-title">Ask me about your class</h2>
            <p className="aa-welcome-sub">
              I have access to real test scores, cycle progress, Q&amp;A activity, and reading stats for <strong>{paper?.name}</strong>. I only cite numbers that are actually in the data.
            </p>
            <div className="aa-suggestions">
              {SUGGESTIONS.map(q => (
                <button
                  key={q}
                  type="button"
                  className="aa-suggestion-chip"
                  onClick={() => handleSend(q)}
                  disabled={loading}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="aa-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`aa-msg aa-msg--${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <div className="aa-msg-bubble aa-msg-bubble--assistant">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="aa-msg-bubble aa-msg-bubble--user">
                    {msg.content}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="aa-msg aa-msg--assistant">
                <div className="aa-msg-bubble aa-msg-bubble--assistant aa-msg-bubble--thinking">
                  <span className="aa-dots">
                    <span /><span /><span />
                  </span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* ── Input row ── */}
      <form
        className="aa-input-row"
        onSubmit={e => { e.preventDefault(); handleSend(); }}
      >
        <input
          ref={inputRef}
          type="text"
          className="aa-input"
          placeholder="Ask about student performance, confusion topics, engagement…"
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="aa-send-btn"
          disabled={loading || !input.trim()}
        >
          {loading ? '…' : 'Ask'}
        </button>
      </form>
    </div>
  );
}
